import config from '../config';
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth } from "../auth/AuthContext";

export const useAssistPointApiFetcher = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { user, getUserProfileIdHeader } = useAuth();

    const getApiUrl = (request, practiceIdOverride) => {
        const pid = practiceIdOverride ?? user?.profile?.practiceId ?? 'unknown';
        return `${config.apiUrl}/practice/${pid}/${request}`;
    };

    const parseValidationError = (data) => {
        if (data && data.forEach) {
            const error = {};
            error.errors = {};
            data.forEach((e) => {
                if (e && e.FieldName) {
                    const keyName =
                        e.FieldName.charAt(0).toLowerCase() + e.FieldName.slice(1);
                    if (Object.keys(error.errors).includes(keyName)) {
                        error.errors[keyName] += e.Message;
                        error.errors[keyName] += "\n";
                    } else {
                        error.errors[keyName] = e.Message;
                        error.errors[keyName] += "\n";
                    }
                }
            });

            return error;
        }
    };

    const handleError = (err) => {
        if (err.response.status === 422) {
            return parseValidationError(err.response.data);
        }

        throw new Error("Unable to process request");
    };

    const fetchGET = async (request, practiceIdOverride) => {
        const token = await getAccessTokenSilently();
        const externalObjectIdHeader = getUserProfileIdHeader();

        return axios
            .get(getApiUrl(request, practiceIdOverride), {
                headers: {
                    'Authorization': 'Bearer ' + token,
                    ...externalObjectIdHeader
                }
            })
            .then((res) => res.data)
            .catch((err) => {
                throw new Error("Unable to process request");
            });
    };

    const fetchPOST = async (request, params) => {
        const token = await getAccessTokenSilently();
        const externalObjectIdHeader = getUserProfileIdHeader();

        return axios
            .post(getApiUrl(request), JSON.stringify(params), {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    'Authorization': 'Bearer ' + token,
                    ...externalObjectIdHeader
                }
            })
            .then((res) => res.data)
            .catch((err) => handleError(err));
    };

    const fetchPUT = async (request, params) => {
        const token = await getAccessTokenSilently();
        const externalObjectIdHeader = getUserProfileIdHeader();

        return axios
            .put(getApiUrl(request), JSON.stringify(params), {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    'Authorization': 'Bearer ' + token,
                    ...externalObjectIdHeader
                }
            })
            .then((res) => res.data)
            .catch((err) => handleError(err));
    };

    const fetchDELETE = async (request, params) => {
        const token = await getAccessTokenSilently();
        const externalObjectIdHeader = getUserProfileIdHeader();

        return axios
            .delete(getApiUrl(request), {
                data: JSON.stringify(params),
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    'Authorization': 'Bearer ' + token,
                    ...externalObjectIdHeader
                }
            })
            .then((res) => res.data)
            .catch((err) => handleError(err));
    };

    const fetchPOSTFORMDATA = async (request, params) => {
        const token = await getAccessTokenSilently();
        const externalObjectIdHeader = getUserProfileIdHeader();

        return axios
            .post(getApiUrl(request), params, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': 'Bearer ' + token,
                    ...externalObjectIdHeader
                }
            })
            .then((res) => res.data)
            .catch((err) => handleError(err));
    };

    const fetchPUTFORMDATA = async (request, params) => {
        const token = await getAccessTokenSilently();
        const externalObjectIdHeader = getUserProfileIdHeader();

        return axios
            .put(getApiUrl(request), params, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': 'Bearer ' + token,
                    ...externalObjectIdHeader
                }
            })
            .then((res) => res.data)
            .catch((err) => handleError(err));
    };

    return {
        fetchGET,
        fetchPOST,
        fetchPUT,
        fetchDELETE,
        fetchPOSTFORMDATA,
        fetchPUTFORMDATA
    };
}
